import { CreditCard, Banknote } from 'lucide-react';
import { Label } from '~/components/ui/label';
import { formatCurrency, calculateCardFee } from '~/utils/paymentUtils';

interface PaymentMethodSelectorProps {
  selectedMethod: string;
  originalMethod?: string;
  onChange: (method: string) => void;
  dueNowTotal?: number;
  tuitionInvoices?: any[];
  showFuturePaymentsInfo?: boolean;
}

/**
 * PaymentMethodSelector - Radio button selector for ACH vs Card payment
 */
export function PaymentMethodSelector({
  selectedMethod,
  originalMethod,
  onChange,
  dueNowTotal = 0,
  tuitionInvoices = [],
  showFuturePaymentsInfo = false
}: PaymentMethodSelectorProps) {
  const cardFeeNow = calculateCardFee(dueNowTotal);
  const totalFutureCardFees = tuitionInvoices.reduce(
    (sum, inv) => sum + calculateCardFee(inv.amount),
    0
  );
  const dueLaterTotal = tuitionInvoices.reduce((sum, inv) => sum + inv.amount, 0);
  const averageMonthlyPayment = tuitionInvoices.length > 0
    ? Math.round(dueLaterTotal / tuitionInvoices.length)
    : 0;

  return (
    <div className="space-y-4">
      <Label className="text-base font-medium">Payment Method</Label>

      {/* ACH Option */}
      <div
        className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
          selectedMethod === 'ach'
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/50'
        }`}
        onClick={() => onChange('ach')}
      >
        <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center ${
          selectedMethod === 'ach' ? 'border-primary' : 'border-muted-foreground'
        }`}>
          {selectedMethod === 'ach' && (
            <div className="w-2 h-2 rounded-full bg-primary" />
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Banknote className="h-4 w-4" />
            <Label className="font-medium cursor-pointer">Pay by Bank (ACH)</Label>
            <span className="text-xs text-green-600 font-medium">Recommended</span>
            {originalMethod === 'ach' && (
              <span className="text-xs text-muted-foreground">(currently selected)</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Pay securely from your bank account. No additional fees.
          </p>
          {dueNowTotal > 0 && (
            <p className="text-sm font-medium mt-1">
              Total: {formatCurrency(dueNowTotal)}
            </p>
          )}
        </div>
      </div>

      {/* Card Option */}
      <div
        className={`flex items-start space-x-3 p-4 border rounded-lg cursor-pointer transition-colors ${
          selectedMethod === 'card'
            ? 'border-primary bg-primary/5'
            : 'border-border hover:border-primary/50'
        }`}
        onClick={() => onChange('card')}
      >
        <div className={`w-4 h-4 rounded-full border-2 mt-0.5 flex items-center justify-center ${
          selectedMethod === 'card' ? 'border-primary' : 'border-muted-foreground'
        }`}>
          {selectedMethod === 'card' && (
            <div className="w-2 h-2 rounded-full bg-primary" />
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            <Label className="font-medium cursor-pointer">Pay by Card</Label>
            {originalMethod === 'card' && (
              <span className="text-xs text-muted-foreground">(currently selected)</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Pay with credit or debit card. Processing fee applies.
          </p>
          {dueNowTotal > 0 && (
            <>
              <p className="text-sm mt-1 text-muted-foreground">
                Subtotal: {formatCurrency(dueNowTotal)} + Fee: {formatCurrency(cardFeeNow)}
              </p>
              <p className="text-sm font-medium">
                Total: {formatCurrency(dueNowTotal + cardFeeNow)}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Future payments info for card */}
      {showFuturePaymentsInfo && selectedMethod === 'card' && tuitionInvoices.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mt-2">
          <p className="text-sm font-medium mb-1">Future Payments</p>
          <p className="text-xs text-muted-foreground">
            {tuitionInvoices.length} payment{tuitionInvoices.length !== 1 ? 's' : ''} totaling {formatCurrency(dueLaterTotal)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Processing fees: {tuitionInvoices.length} × (2.9% × {formatCurrency(averageMonthlyPayment)} + $0.30) = {formatCurrency(totalFutureCardFees)}
          </p>
        </div>
      )}
    </div>
  );
}

export default PaymentMethodSelector;
